import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { identityLogout } from "../lib/identityApi";
import { IdentityLayout } from "@speedycore/identity-ui";

const HUB_DOMAINS = [
  "https://constructiveos.com.au/",
  "https://mytradieweb.com.au/",
  "https://creatorshub.com.au/",
  "https://hubcircles.com.au/",
  "https://adverthub.com.au/",
  "https://buildersbrochures.com.au/",
  "https://speedycore.com.au/",
  "https://dashboard.speedycore.com.au/",
  "https://ide.speedycore.com.au/",
] as const;

const CLEAR_QUERY = "__sc_logout_clear=1";

export function HubLogoutPage() {
  const [params] = useSearchParams();
  const postLogoutRedirect = useMemo(() => {
    const raw = params.get("post_logout_redirect") ?? params.get("redirect_uri");
    if (!raw) return null;
    try {
      const u = new URL(raw, window.location.origin);
      if (u.protocol !== "http:" && u.protocol !== "https:") return null;
      return u.toString();
    } catch {
      return null;
    }
  }, [params]);

  const [clearedCount, setClearedCount] = useState(0);
  const total = HUB_DOMAINS.length;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await identityLogout();
      if (cancelled) return;

      const clearOrigins = async () => {
        for (const origin of HUB_DOMAINS) {
          try {
            await new Promise<void>((resolve) => {
              const iframe = document.createElement("iframe");
              const clearUrl = new URL(origin);
              clearUrl.searchParams.set("__sc_logout_clear", "1");
              clearUrl.searchParams.set("__sc_from", "auth.logout");
              iframe.src = clearUrl.toString();
              iframe.style.display = "none";
              iframe.setAttribute("aria-hidden", "true");
              iframe.setAttribute("title", "logout sync");
              let settled = false;
              const finish = () => {
                if (settled) return;
                settled = true;
                try {
                  if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
                } catch {
                  /* noop */
                }
                resolve();
              };
              iframe.onload = () => {
                window.setTimeout(finish, 250);
              };
              iframe.onerror = finish;
              window.setTimeout(finish, 1800);
              document.body.appendChild(iframe);
            });
          } catch {
            /* noop */
          }
          if (!cancelled) setClearedCount((c) => Math.min(c + 1, total));
        }
      };

      await clearOrigins();
      if (cancelled) return;

      window.setTimeout(() => {
        const target = postLogoutRedirect ?? "https://speedycore.com.au/";
        window.location.assign(target);
      }, 150);
    })();
    return () => {
      cancelled = true;
    };
  }, [postLogoutRedirect, total]);

  const pct = Math.min(100, Math.round((clearedCount / Math.max(1, total)) * 100));

  return (
    <IdentityLayout
      hero={
        <div style={{ textAlign: "center" }}>
          <h1 style={{ margin: 0, fontSize: "1.75rem" }}>Signing you out…</h1>
          <p style={{ margin: "0.75rem 0 0", color: "#475569", lineHeight: 1.45 }}>
            Clearing your session on MyTradieWeb, ConstructiveOS, CreatorHub, HubCircles,
            AdvertHub, and SpeedyCore so you are signed out everywhere.
          </p>
        </div>
      }
      form={
        <div
          style={{
            display: "grid",
            gap: "0.75rem",
            padding: "0.25rem",
          }}
        >
          <div
            style={{
              height: 10,
              width: "100%",
              background: "#e2e8f0",
              borderRadius: 999,
              overflow: "hidden",
            }}
            aria-hidden
          >
            <div
              style={{
                height: "100%",
                width: `${pct}%`,
                background: "linear-gradient(90deg, #22c55e, #3b82f6)",
                transition: "width 180ms ease",
              }}
            />
          </div>
          <p style={{ margin: 0, textAlign: "center", fontSize: "0.9rem", color: "#475569" }}>
            {clearedCount} of {total} sites cleared
          </p>
          <p style={{ margin: 0, textAlign: "center", fontSize: "0.85rem", color: "#64748b" }}>
            Redirecting to{" "}
            <a
              href={postLogoutRedirect ?? "https://speedycore.com.au/"}
              style={{ color: "#2563eb" }}
            >
              {postLogoutRedirect ?? "speedycore.com.au"}
            </a>{" "}
            when complete.
          </p>
        </div>
      }
    />
  );
}

export const HubLogoutClearQuery = CLEAR_QUERY;
